import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "../../api/client.js";
import { marketplaceApi } from "../../api/endpoints.js";
import type { PerfilPropio } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Field, TextInput, ErrorBanner, InfoBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";

const ESTADO_VERIF: Record<string, string> = {
  pendiente: "Pendiente",
  verificada: "Verificada ✓",
  rechazada: "Rechazada",
};

export function ProfilePage() {
  const t = useT();

  // ---- perfil (carga tolerante a 404) ------------------------------------
  const [cargandoPerfil, setCargandoPerfil] = useState(true);
  const [perfil, setPerfil] = useState<PerfilPropio | null>(null);
  const [nombreProfesional, setNombreProfesional] = useState("");
  const [pais, setPais] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [especialidades, setEspecialidades] = useState("");
  const [bio, setBio] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");
  const [contactoWhatsapp, setContactoWhatsapp] = useState("");
  const [perfilError, setPerfilError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [accionError, setAccionError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  function hidratar(p: PerfilPropio) {
    setPerfil(p);
    setNombreProfesional(p.nombreProfesional);
    setPais(p.pais);
    setCiudad(p.ciudad);
    setEspecialidades(p.especialidades.join(", "));
    setBio(p.bio ?? "");
    setContactoEmail(p.contactoEmail ?? "");
    setContactoWhatsapp(p.contactoWhatsapp ?? "");
  }

  useEffect(() => {
    let vivo = true;
    setCargandoPerfil(true);
    marketplaceApi
      .miPerfil()
      .then((p) => {
        if (vivo) hidratar(p);
      })
      .catch((e: unknown) => {
        // 404 = aún no hay perfil; el formulario queda vacío.
        if (vivo && !(e instanceof ApiError && e.status === 404)) {
          setPerfilError(mensajeDeError(e));
        }
      })
      .finally(() => {
        if (vivo) setCargandoPerfil(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const verificaciones = useQuery(() => marketplaceApi.misVerificaciones());

  async function onGuardar(ev: FormEvent) {
    ev.preventDefault();
    setPerfilError(null);
    setGuardando(true);
    try {
      const esp = especialidades
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const bioTrim = bio.trim();
      const emailTrim = contactoEmail.trim();
      const waTrim = contactoWhatsapp.trim();
      const p = await marketplaceApi.guardarPerfil({
        nombreProfesional: nombreProfesional.trim(),
        pais: pais.trim(),
        ciudad: ciudad.trim(),
        ...(esp.length > 0 ? { especialidades: esp } : {}),
        ...(bioTrim ? { bio: bioTrim } : {}),
        ...(emailTrim ? { contactoEmail: emailTrim } : {}),
        ...(waTrim ? { contactoWhatsapp: waTrim } : {}),
      });
      hidratar(p);
    } catch (e) {
      setPerfilError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  async function publicacion(accion: () => Promise<PerfilPropio>) {
    setAccionError(null);
    setOcupado(true);
    try {
      hidratar(await accion());
    } catch (e) {
      setAccionError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  // ---- verificación ------------------------------------------------------
  const [verifPais, setVerifPais] = useState("");
  const [numeroColegiatura, setNumeroColegiatura] = useState("");
  const [documentoRef, setDocumentoRef] = useState("");
  const [verifError, setVerifError] = useState<string | null>(null);
  const [enviandoVerif, setEnviandoVerif] = useState(false);

  async function onEnviarVerif(ev: FormEvent) {
    ev.preventDefault();
    setVerifError(null);
    setEnviandoVerif(true);
    try {
      const doc = documentoRef.trim();
      await marketplaceApi.enviarVerificacion({
        pais: verifPais.trim(),
        numeroColegiatura: numeroColegiatura.trim(),
        ...(doc ? { documentoRef: doc } : {}),
      });
      setVerifPais("");
      setNumeroColegiatura("");
      setDocumentoRef("");
      verificaciones.reload();
    } catch (e) {
      setVerifError(mensajeDeError(e));
    } finally {
      setEnviandoVerif(false);
    }
  }

  const verificado = perfil?.credencialVerificada ?? false;

  return (
    <>
      <ScreenHeader title={t("therapist.profile.title")} />
      <Content>
        {cargandoPerfil ? (
          <Loader label={t("common.loading")} />
        ) : (
          <>
            <Card>
              <h2>{t("therapist.profile.title")}</h2>
              <form onSubmit={onGuardar} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
                {perfilError ? <ErrorBanner message={perfilError} /> : null}
                <Field label="Nombre profesional">
                  <TextInput
                    required
                    value={nombreProfesional}
                    onChange={(e) => setNombreProfesional(e.target.value)}
                  />
                </Field>
                <Field label="País">
                  <TextInput required value={pais} onChange={(e) => setPais(e.target.value)} />
                </Field>
                <Field label="Ciudad">
                  <TextInput required value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
                </Field>
                <Field label="Especialidades" hint="Separadas por comas">
                  <TextInput
                    value={especialidades}
                    onChange={(e) => setEspecialidades(e.target.value)}
                  />
                </Field>
                <Field label="Bio">
                  <TextInput value={bio} onChange={(e) => setBio(e.target.value)} />
                </Field>
                <Field label="Correo de contacto">
                  <TextInput
                    type="email"
                    value={contactoEmail}
                    onChange={(e) => setContactoEmail(e.target.value)}
                  />
                </Field>
                <Field label="WhatsApp de contacto">
                  <TextInput
                    value={contactoWhatsapp}
                    onChange={(e) => setContactoWhatsapp(e.target.value)}
                  />
                </Field>
                <Button type="submit" block disabled={guardando}>
                  {guardando ? t("common.saving") : t("common.save")}
                </Button>
              </form>
            </Card>

            <Card>
              <h2>Publicación</h2>
              {accionError ? <ErrorBanner message={accionError} /> : null}
              <p style={{ marginTop: 4 }}>
                Credencial:{" "}
                <strong>{verificado ? "verificada ✓" : "no verificada"}</strong> · Estado:{" "}
                <strong>{perfil?.publicado ? "publicado" : "no publicado"}</strong>
              </p>
              {!verificado ? (
                <InfoBanner tone="warn">{t("therapist.profile.needVerif")}</InfoBanner>
              ) : null}
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <Button
                  disabled={ocupado || !perfil || !verificado}
                  onClick={() => void publicacion(() => marketplaceApi.publicar())}
                >
                  {t("therapist.profile.publish")}
                </Button>
                <Button
                  variant="ghost"
                  disabled={ocupado || !perfil}
                  onClick={() => void publicacion(() => marketplaceApi.despublicar())}
                >
                  {t("therapist.profile.unpublish")}
                </Button>
              </div>
            </Card>

            <Card>
              <h2>{t("therapist.profile.credential")}</h2>
              {verificaciones.loading ? (
                <Loader label={t("common.loading")} />
              ) : verificaciones.data && verificaciones.data.length > 0 ? (
                <ul style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                  {verificaciones.data.map((v) => (
                    <li key={v.id} style={{ fontSize: 14 }}>
                      {v.pais} · {v.numeroColegiatura} —{" "}
                      <strong>{ESTADO_VERIF[v.estado] ?? v.estado}</strong>
                      {v.motivoRechazo ? (
                        <span style={{ color: "var(--ink-soft)" }}> ({v.motivoRechazo})</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Aún no enviaste verificaciones.</p>
              )}

              <form onSubmit={onEnviarVerif} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                {verifError ? <ErrorBanner message={verifError} /> : null}
                <Field label="País">
                  <TextInput required value={verifPais} onChange={(e) => setVerifPais(e.target.value)} />
                </Field>
                <Field label="Número de colegiatura">
                  <TextInput
                    required
                    value={numeroColegiatura}
                    onChange={(e) => setNumeroColegiatura(e.target.value)}
                  />
                </Field>
                <Field label="Referencia de documento" hint="Opcional">
                  <TextInput value={documentoRef} onChange={(e) => setDocumentoRef(e.target.value)} />
                </Field>
                <Button
                  type="submit"
                  variant="sun"
                  block
                  disabled={enviandoVerif || !verifPais.trim() || !numeroColegiatura.trim()}
                >
                  {enviandoVerif ? t("common.saving") : t("therapist.profile.credential")}
                </Button>
              </form>
            </Card>
          </>
        )}
      </Content>
    </>
  );
}
